/**
 * PublicLayout — Layout for marketing pages (homepage, pricing, faq, login).
 * Includes nav bar with logo and CTA, plus footer.
 *
 * Auth-aware: When logged in, shows "Tribe Space" link and avatar dropdown
 * instead of "Log In" / "Start Free Trial".
 * Logo navigates to /app when logged in, / when logged out.
 * Mobile: hamburger icon opens a slide-in drawer.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { BRAND } from '../../config/branding';
import Logo from '../common/Logo';
import MobileDrawer from './MobileDrawer';
import {
  ChevronDown, User, Users, Settings, HelpCircle,
  LogOut, FileText, Shield, Menu,
} from 'lucide-react';

const PublicLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, signOut } = useAuth();
  const isHome = location.pathname === '/';

  const isPrimary = user?.role === 'primary';

  // Avatar dropdown state (desktop)
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Mobile drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNav = (path: string) => {
    setMenuOpen(false);
    navigate(path);
  };

  const handleSignOut = () => {
    setMenuOpen(false);
    signOut();
    navigate('/');
  };

  return (
    <div className="public-layout">
      {/* Navigation */}
      <nav className="public-nav">
        <div className="nav-inner">
          <Link to={isAuthenticated ? '/app' : '/'} className="nav-logo">
            <Logo size={75} />
          </Link>

          {/* Desktop nav links */}
          <div className="nav-links">
            <Link to="/" className={`nav-link ${isHome ? 'active' : ''}`}>Home</Link>
            <Link to="/about" className="nav-link">About</Link>
            <Link to="/pricing" className="nav-link">Pricing</Link>
            <Link to="/faq" className="nav-link">FAQ</Link>
            <Link to="/contact" className="nav-link">Contact</Link>

            {isAuthenticated ? (
              <>
                <Link to="/app" className="nav-link">Tribe Space</Link>

                {/* Avatar dropdown */}
                <div className="topbar-right" ref={menuRef}>
                  <button
                    className="topbar-user-btn"
                    onClick={() => setMenuOpen(!menuOpen)}
                    aria-expanded={menuOpen}
                    aria-haspopup="true"
                  >
                    <span className="topbar-avatar">
                      {user?.displayName?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                    <span className="topbar-user-name">{user?.displayName}</span>
                    <ChevronDown size={16} className={`topbar-chevron ${menuOpen ? 'open' : ''}`} />
                  </button>

                  {menuOpen && (
                    <div className="topbar-dropdown">
                      <div className="dropdown-header">
                        <span className="dropdown-name">{user?.displayName}</span>
                        <span className="dropdown-role">
                          {isPrimary ? 'Primary User' : 'Member'}
                        </span>
                      </div>
                      <div className="dropdown-divider" />

                      <button className="dropdown-item" onClick={() => handleNav('/app/profile')}>
                        <User size={16} /> Profile
                      </button>
                      <button className="dropdown-item" onClick={() => handleNav('/app/users')}>
                        <Users size={16} /> My Tribe
                      </button>
                      {isPrimary && (
                        <button className="dropdown-item" onClick={() => handleNav('/app/settings')}>
                          <Settings size={16} /> Settings
                        </button>
                      )}
                      <button className="dropdown-item" onClick={() => handleNav('/app/support')}>
                        <HelpCircle size={16} /> Support
                      </button>

                      <div className="dropdown-divider" />
                      <button className="dropdown-item" onClick={() => handleNav('/terms')}>
                        <FileText size={16} /> Terms of Service
                      </button>
                      <button className="dropdown-item" onClick={() => handleNav('/privacy')}>
                        <Shield size={16} /> Privacy Policy
                      </button>

                      {user?.isAdmin && (
                        <>
                          <div className="dropdown-divider" />
                          <button className="dropdown-item" onClick={() => handleNav('/admin')}>
                            <Settings size={16} /> Admin Dashboard
                          </button>
                        </>
                      )}

                      <div className="dropdown-divider" />
                      <button className="dropdown-item dropdown-item--danger" onClick={handleSignOut}>
                        <LogOut size={16} /> Sign Out
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link to="/login" className="nav-link">Log In</Link>
                <Link to="/signup" className="nav-cta">Start Free Trial</Link>
              </>
            )}
          </div>

          {/* Mobile hamburger button */}
          <button
            className="nav-hamburger"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
          >
            <Menu size={24} />
          </button>
        </div>
      </nav>

      {/* Mobile drawer */}
      <MobileDrawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} />

      {/* Page content */}
      <main className="public-main">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="public-footer">
        <div className="footer-inner">
          <div className="footer-brand">
            <Logo size={24} />
          </div>
          <div className="footer-links">
            <Link to="/about">About</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
            <Link to="/faq">FAQ</Link>
            <Link to="/contact">Contact</Link>
          </div>
          <div className="footer-copyright">
            &copy; {new Date().getFullYear()} {BRAND.company}. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;